<script setup lang="ts">
import type { CalendarRootEmits, CalendarRootProps } from 'reka-ui';
import { CalendarCell, CalendarCellTrigger, CalendarGrid, CalendarGridBody, CalendarGridHead, CalendarGridRow, CalendarHeadCell, CalendarHeader, CalendarHeading, CalendarNext, CalendarPrev, CalendarRoot, useForwardPropsEmits } from 'reka-ui';
import { ChevronLeft, ChevronRight } from '@lucide/vue';

const props = withDefaults(defineProps<CalendarRootProps>(), {
  locale: 'zh-CN',
  weekdayFormat: 'narrow',
  weekStartsOn: 0,
});
const emits = defineEmits<CalendarRootEmits>();

const forwarded = useForwardPropsEmits(props, emits);
</script>

<template>
  <CalendarRoot
    v-slot="{ grid, weekDays }"
    data-slot="calendar"
    v-bind="forwarded"
    class="p-3"
  >
    <CalendarHeader class="flex w-full items-center justify-between mb-2">
      <CalendarHeading class="text-text-primary text-sm font-semibold" />
      <div class="flex items-center gap-1">
        <CalendarPrev
          class="flex items-center justify-center size-7 rounded-md text-text-tertiary transition hover:bg-primary-subtle hover:text-primary cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
        >
          <ChevronLeft class="size-4" />
        </CalendarPrev>
        <CalendarNext
          class="flex items-center justify-center size-7 rounded-md text-text-tertiary transition hover:bg-primary-subtle hover:text-primary cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
        >
          <ChevronRight class="size-4" />
        </CalendarNext>
      </div>
    </CalendarHeader>

    <CalendarGrid
      v-for="(month, idx) in grid"
      :key="idx"
      class="mt-2 w-full border-collapse"
    >
      <CalendarGridHead>
        <CalendarGridRow>
          <CalendarHeadCell
            v-for="(d, i) in weekDays"
            :key="i"
            class="w-9 pb-1.5 text-text-tertiary text-xs font-normal"
          >
            {{ d }}
          </CalendarHeadCell>
        </CalendarGridRow>
      </CalendarGridHead>
      <CalendarGridBody>
        <CalendarGridRow v-for="(row, i) in month.rows" :key="i" class="mt-0.5">
          <CalendarCell
            v-for="date in row"
            :key="date.toString()"
            :date="date"
            class="relative p-0 text-center text-sm"
          >
            <CalendarCellTrigger
              v-slot="{ dayValue }"
              :day="date"
              :month="month.value"
              class="size-9 flex items-center justify-center rounded-md p-0 font-normal text-text-secondary cursor-pointer transition hover:bg-primary-subtle data-[selected]:bg-primary data-[selected]:text-white data-[selected]:font-semibold data-[disabled]:opacity-40 data-[disabled]:pointer-events-none data-[outside-view]:text-text-tertiary/30 data-[unavailable]:text-text-tertiary/40 data-[unavailable]:line-through data-[today]:ring-1 data-[today]:ring-primary/40"
            >
              {{ dayValue }}
            </CalendarCellTrigger>
          </CalendarCell>
        </CalendarGridRow>
      </CalendarGridBody>
    </CalendarGrid>
  </CalendarRoot>
</template>
